import { useState } from 'react';
import {
  Mail,
  X,
  Eye,
  Code,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  MessageSquare,
  Bell,
  UserPlus,
  Shield,
  Package,
} from 'lucide-react';

type TemplateType = 'otp' | 'welcome' | 'invitation' | 'new-complaint' | 'status-update' | 'comment';

interface TemplateInfo {
  id: TemplateType;
  name: string;
  description: string;
  icon: React.ReactNode;
  subject: string;
  html: string;
  plainText: string;
}

// Sample data for template previews
const sampleData = {
  code: '847291',
  expiryMinutes: 10,
  firstName: 'John',
  email: 'john.smith@acmecorp.com',
  inviterName: 'Sarah Johnson',
  vendorName: 'Acme Corp',
  complaintId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  referenceNumber: 'TBC-24-01-042',
  webAppBaseUrl: 'https://dev.quality-complaints.mapp.superapp.yum.com',
  brandId: 'TBC',
  environment: 'dev',
  storeName: 'Store #1234 - Downtown',
  productName: 'Premium Chicken Wings',
  category: 'Product Quality',
  description: 'The chicken wings received in the latest delivery appeared discolored and had an unusual smell. The product was immediately quarantined and not served to customers.',
  supplierName: 'Acme Food Supplies',
  recipientName: 'John Smith',
  previousStatus: 'Submitted',
  newStatus: 'Under Review',
  comment: 'We have received your complaint and are investigating the issue with our quality control team. We will provide an update within 48 hours.',
  commenterName: 'Maria Garcia',
  commenterType: 'vendor' as const,
};

// Generate HTML templates with sample data
function generateTemplates(): TemplateInfo[] {
  const currentYear = new Date().getFullYear();
  const complaintUrl = `${sampleData.webAppBaseUrl}/complaints/${sampleData.complaintId}?brand=${encodeURIComponent(sampleData.brandId)}&environment=${encodeURIComponent(sampleData.environment)}`;
  const portalHomeUrl = sampleData.webAppBaseUrl.replace(/\/+$/, '');
  const portalPlainText = `\n\nGo to the portal: ${portalHomeUrl}\n`;
  const ctaButton = `
    <div style="text-align: center; margin: 24px 0;">
      <a
        href="${complaintUrl}"
        style="
          display: inline-block;
          background-color: #2563eb;
          color: #ffffff;
          padding: 12px 18px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
        "
        target="_blank"
        rel="noopener noreferrer"
      >
        View complaint
      </a>
    </div>
  `;
  const portalButton = `
    <div style="text-align: center; margin: 24px 0;">
      <a
        href="${portalHomeUrl}"
        style="
          display: inline-block;
          background-color: #2563eb;
          color: #ffffff;
          padding: 12px 18px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
        "
        target="_blank"
        rel="noopener noreferrer"
      >
        Go to the portal
      </a>
    </div>
  `;

  return [
    {
      id: 'otp',
      name: 'OTP Login Code',
      description: 'Sent when a vendor user requests a login code',
      icon: <Shield className="w-5 h-5" />,
      subject: 'Your Login Code for Byte Quality Complaints',
      html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 0;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #ffffff;
    }
    .header {
      text-align: center;
      padding: 20px 0;
      border-bottom: 2px solid #2563eb;
    }
    .header h1 {
      margin: 0;
      color: #2563eb;
      font-size: 24px;
    }
    .content {
      padding: 30px 20px;
    }
    .code-container {
      text-align: center;
      margin: 30px 0;
    }
    .code {
      font-size: 36px;
      font-weight: bold;
      letter-spacing: 10px;
      color: #1f2937;
      padding: 20px;
      background-color: #f3f4f6;
      border-radius: 8px;
      display: inline-block;
    }
    .warning {
      background-color: #fef2f2;
      border-left: 4px solid #ef4444;
      padding: 12px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .warning p {
      margin: 0;
      color: #991b1b;
      font-size: 14px;
    }
    .footer {
      text-align: center;
      padding: 20px;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 12px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Byte Quality Complaints</h1>
    </div>
    <div class="content">
      <h2>Your Login Code</h2>
      <p>Enter this code to complete your login to Byte Quality Complaints:</p>
      <div class="code-container">
        <div class="code">${sampleData.code}</div>
      </div>
            <div class="warning">
              <p><strong>⏱️ This code will expire in ${sampleData.expiryMinutes} minutes.</strong></p>
              <p>Do not share this code with anyone.</p>
            </div>
      ${portalButton}
      <p>If you didn't request this code, please ignore this email or contact your administrator if you have concerns.</p>
    </div>
    <div class="footer">
      <p>&copy; ${currentYear} Yum! Brands. All rights reserved.</p>
      <p>This is an automated message. Please do not reply to this email.</p>
    </div>
  </div>
</body>
</html>
      `,
      plainText: `Byte Quality Complaints - Your Login Code

Your login code is: ${sampleData.code}

This code will expire in ${sampleData.expiryMinutes} minutes.

Do not share this code with anyone.

If you didn't request this code, please ignore this email.
${portalPlainText}
© ${currentYear} Yum! Brands. All rights reserved.`,
    },
    {
      id: 'welcome',
      name: 'Welcome Email',
      description: 'Sent when a new vendor user completes onboarding',
      icon: <UserPlus className="w-5 h-5" />,
      subject: 'Welcome to Byte Quality Complaints!',
      html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 0;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #ffffff;
    }
    .header {
      text-align: center;
      padding: 20px 0;
      background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
      color: white;
      border-radius: 8px 8px 0 0;
    }
    .header h1 {
      margin: 0;
      font-size: 28px;
    }
    .content {
      padding: 30px 20px;
    }
    .content h2 {
      color: #2563eb;
    }
    .features {
      background-color: #f9fafb;
      padding: 20px;
      border-radius: 8px;
      margin: 20px 0;
    }
    .features ul {
      margin: 10px 0;
      padding-left: 20px;
    }
    .features li {
      margin: 8px 0;
    }
    .footer {
      text-align: center;
      padding: 20px;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 12px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Welcome to Byte Quality Complaints!</h1>
    </div>
    <div class="content">
            <h2>Hello ${sampleData.firstName}! 👋</h2>
            <p>Your account has been successfully created. You now have access to Byte Quality Complaints, your portal for managing quality complaints and store data.</p>

      <div class="features">
        <h3>What you can do:</h3>
        <ul>
          <li>View and manage quality complaints</li>
          <li>Access store performance data</li>
          <li>Generate reports and analytics</li>
          <li>Collaborate with your team</li>
        </ul>
      </div>

      ${portalButton}
      <p>If you have any questions or need assistance, please don't hesitate to contact your administrator.</p>

      <p>Best regards,<br>The Byte Quality Complaints Team</p>
    </div>
    <div class="footer">
      <p>&copy; ${currentYear} Yum! Brands. All rights reserved.</p>
    </div>
  </div>
</body>
</html>
      `,
      plainText: `Welcome to Byte Quality Complaints!

Hello ${sampleData.firstName}!

Your account has been successfully created. You now have access to Byte Quality Complaints, your portal for managing quality complaints and store data.

What you can do:
- View and manage quality complaints
- Access store performance data
- Generate reports and analytics
- Collaborate with your team
${portalPlainText}
If you have any questions or need assistance, please don't hesitate to contact your administrator.

Best regards,
The Byte Quality Complaints Team

© ${currentYear} Yum! Brands. All rights reserved.`,
    },
    {
      id: 'invitation',
      name: 'Vendor Invitation',
      description: 'Sent when inviting a new vendor user to the platform',
      icon: <Mail className="w-5 h-5" />,
      subject: `You've been invited to Byte Quality Complaints for ${sampleData.vendorName}`,
      html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 0;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #ffffff;
    }
    .header {
      text-align: center;
      padding: 20px 0;
      background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
      color: white;
      border-radius: 8px 8px 0 0;
    }
    .header h1 {
      margin: 0;
      font-size: 28px;
    }
    .content {
      padding: 30px 20px;
    }
    .content h2 {
      color: #2563eb;
    }
    .invitation-box {
      background-color: #f0f9ff;
      border-left: 4px solid #2563eb;
      padding: 15px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .vendor-badge {
      display: inline-block;
      background-color: #dbeafe;
      color: #1e40af;
      padding: 4px 12px;
      border-radius: 16px;
      font-weight: 500;
      margin-top: 8px;
    }
    .footer {
      text-align: center;
      padding: 20px;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 12px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>You're Invited!</h1>
    </div>
    <div class="content">
      <h2>Hello!</h2>
      <div class="invitation-box">
        <p><strong>${sampleData.inviterName}</strong> has invited you to access Byte Quality Complaints.</p>
        <p class="vendor-badge">Vendor: ${sampleData.vendorName}</p>
      </div>
      <p>Byte Quality Complaints is a platform for managing quality complaints and accessing store data. You've been granted access with specific permissions as part of the ${sampleData.vendorName} vendor organization.</p>
      <p><strong>To get started:</strong></p>
      <ol>
        <li>Visit the Byte Quality Complaints login page</li>
        <li>Select "Sign in with Email"</li>
        <li>Enter your email address (${sampleData.email})</li>
        <li>You'll receive a verification code to complete your login</li>
      </ol>
      ${portalButton}
      <p>This invitation is valid for 7 days.</p>
      <p>If you have any questions, please contact ${sampleData.inviterName} or your administrator.</p>
    </div>
    <div class="footer">
      <p>&copy; ${currentYear} Yum! Brands. All rights reserved.</p>
    </div>
  </div>
</body>
</html>
      `,
      plainText: `You're Invited to Byte Quality Complaints!

${sampleData.inviterName} has invited you to access Byte Quality Complaints for ${sampleData.vendorName}.

Byte Quality Complaints is a platform for managing quality complaints and accessing store data. You've been granted access with specific permissions as part of the ${sampleData.vendorName} vendor organization.

To get started:
1. Visit the Byte Quality Complaints login page
2. Select "Sign in with Email"
3. Enter your email address (${sampleData.email})
4. You'll receive a verification code to complete your login
${portalPlainText}
This invitation is valid for 7 days.

If you have any questions, please contact ${sampleData.inviterName} or your administrator.

© ${currentYear} Yum! Brands. All rights reserved.`,
    },
    {
      id: 'new-complaint',
      name: 'New Complaint Notification',
      description: 'Sent to distributors when a new complaint is submitted',
      icon: <AlertCircle className="w-5 h-5" />,
      subject: `[${sampleData.referenceNumber}] New Quality Complaint: ${sampleData.productName} - ${sampleData.storeName}`,
      html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 0;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #ffffff;
    }
    .header {
      text-align: center;
      padding: 20px 0;
      background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
      color: white;
      border-radius: 8px 8px 0 0;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
    }
    .content {
      padding: 30px 20px;
    }
    .alert-box {
      background-color: #fef2f2;
      border-left: 4px solid #dc2626;
      padding: 15px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .details-table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
    }
    .details-table td {
      padding: 10px;
      border-bottom: 1px solid #e5e7eb;
    }
    .details-table td:first-child {
      font-weight: 600;
      color: #6b7280;
      width: 120px;
    }
    .description-box {
      background-color: #f9fafb;
      padding: 15px;
      border-radius: 8px;
      margin: 15px 0;
    }
    .footer {
      text-align: center;
      padding: 20px;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 12px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>New Quality Complaint</h1>
    </div>
    <div class="content">
      <div class="alert-box">
        <p><strong>A new quality complaint has been submitted for ${sampleData.supplierName}.</strong></p>
        <p>Please review and respond to this complaint.</p>
      </div>

            <table class="details-table">
              <tr>
                <td>Reference #</td>
                <td><strong>${sampleData.referenceNumber}</strong></td>
              </tr>
              <tr>
                <td>Restaurant</td>
                <td>${sampleData.storeName}</td>
              </tr>
              <tr>
                <td>Product</td>
                <td>${sampleData.productName}</td>
              </tr>
              <tr>
                <td>Category</td>
                <td>${sampleData.category}</td>
              </tr>
            </table>

            <h3>Description</h3>
            <div class="description-box">
              <p>${sampleData.description}</p>
            </div>

      ${ctaButton}
            <p>Please log in to Byte Quality Complaints to view the full details and respond to this complaint.</p>
          </div>
          <div class="footer">
            <p>&copy; ${currentYear} Yum! Brands. All rights reserved.</p>
            <p>This is an automated message. Please do not reply to this email.</p>
          </div>
        </div>
      </body>
      </html>
      `,
      plainText: `New Quality Complaint

A new quality complaint has been submitted for ${sampleData.supplierName}.

Reference #: ${sampleData.referenceNumber}
Restaurant: ${sampleData.storeName}
Product: ${sampleData.productName}
Category: ${sampleData.category}

Description:
${sampleData.description}

View complaint: ${complaintUrl}

Please log in to Byte Quality Complaints to view the full details and respond to this complaint.

© ${currentYear} Yum! Brands. All rights reserved.`,
    },
    {
      id: 'status-update',
      name: 'Status Update Notification',
      description: 'Sent to store submitter when complaint status changes',
      icon: <Bell className="w-5 h-5" />,
      subject: `[${sampleData.referenceNumber}] Complaint ${sampleData.newStatus}: ${sampleData.productName}`,
      html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 0;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #ffffff;
    }
    .header {
      text-align: center;
      padding: 20px 0;
      background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
      color: white;
      border-radius: 8px 8px 0 0;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
    }
    .content {
      padding: 30px 20px;
    }
    .status-badge {
      display: inline-block;
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: 600;
      font-size: 14px;
      color: white;
      background-color: #f59e0b;
    }
    .status-update-box {
      background-color: #f9fafb;
      padding: 20px;
      border-radius: 8px;
      margin: 20px 0;
      text-align: center;
    }
    .details-table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
    }
    .details-table td {
      padding: 10px;
      border-bottom: 1px solid #e5e7eb;
    }
    .details-table td:first-child {
      font-weight: 600;
      color: #6b7280;
      width: 120px;
    }
    .comment-box {
      background-color: #f0f9ff;
      border-left: 4px solid #2563eb;
      padding: 15px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .footer {
      text-align: center;
      padding: 20px;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 12px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Complaint Status Update</h1>
    </div>
    <div class="content">
      <p>Hello ${sampleData.recipientName},</p>

      <div class="status-update-box">
        <p style="margin-bottom: 15px; color: #6b7280;">Your complaint status has been updated to:</p>
        <span class="status-badge">${sampleData.newStatus}</span>
      </div>

            <table class="details-table">
              <tr>
                <td>Reference #</td>
                <td><strong>${sampleData.referenceNumber}</strong></td>
              </tr>
              <tr>
                <td>Product</td>
                <td>${sampleData.productName}</td>
              </tr>
              <tr>
                <td>Distributor</td>
                <td>${sampleData.supplierName}</td>
              </tr>
            </table>

            <h3>Distributor Comment</h3>
            <div class="comment-box">
              <p>${sampleData.comment}</p>
            </div>

      ${ctaButton}
            <p>Log in to Byte Quality Complaints to view the full complaint details.</p>
          </div>
          <div class="footer">
            <p>&copy; ${currentYear} Yum! Brands. All rights reserved.</p>
            <p>This is an automated message. Please do not reply to this email.</p>
          </div>
        </div>
      </body>
      </html>
      `,
      plainText: `Complaint Status Update

Hello ${sampleData.recipientName},

Your complaint status has been updated to: ${sampleData.newStatus}

Reference #: ${sampleData.referenceNumber}
Product: ${sampleData.productName}
Distributor: ${sampleData.supplierName}

Distributor Comment:
${sampleData.comment}

View complaint: ${complaintUrl}

Log in to Byte Quality Complaints to view the full complaint details.

© ${currentYear} Yum! Brands. All rights reserved.`,
    },
    {
      id: 'comment',
      name: 'Comment Notification',
      description: 'Sent when a comment is added to a complaint',
      icon: <MessageSquare className="w-5 h-5" />,
      subject: `[${sampleData.referenceNumber}] New Comment on Complaint: ${sampleData.productName}`,
      html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 0;
      background-color: #f5f5f5;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #ffffff;
    }
    .header {
      text-align: center;
      padding: 20px 0;
      background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
      color: white;
      border-radius: 8px 8px 0 0;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
    }
    .content {
      padding: 30px 20px;
    }
    .comment-box {
      background-color: #f9fafb;
      border-left: 4px solid #2563eb;
      padding: 20px;
      margin: 20px 0;
      border-radius: 4px;
    }
          .commenter-badge {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
            background-color: ${sampleData.commenterType === 'vendor' ? '#dbeafe' : '#dcfce7'};
            color: ${sampleData.commenterType === 'vendor' ? '#1e40af' : '#166534'};
            margin-left: 10px;
          }
    .footer {
      text-align: center;
      padding: 20px;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 12px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>New Comment</h1>
    </div>
    <div class="content">
      <p>Hello ${sampleData.recipientName},</p>
      <p>A new comment has been added to complaint <strong>${sampleData.referenceNumber}</strong> for <strong>${sampleData.productName}</strong>.</p>

            <div class="comment-box">
              <div style="margin-bottom: 10px;">
                <strong>${sampleData.commenterName}</strong>
                <span class="commenter-badge">${sampleData.commenterType === 'vendor' ? 'Distributor' : 'Store'}</span>
              </div>
              <p style="margin: 0;">${sampleData.comment}</p>
            </div>

            ${ctaButton}
            <p>Log in to Byte Quality Complaints to view the full conversation and respond.</p>
          </div>
          <div class="footer">
            <p>&copy; ${currentYear} Yum! Brands. All rights reserved.</p>
            <p>This is an automated message. Please do not reply to this email.</p>
          </div>
        </div>
      </body>
      </html>
      `,
      plainText: `New Comment on Complaint

Hello ${sampleData.recipientName},

A new comment has been added to complaint ${sampleData.referenceNumber} for ${sampleData.productName}.

From: ${sampleData.commenterName} (${sampleData.commenterType === 'vendor' ? 'Distributor' : 'Store'})

Comment:
${sampleData.comment}

View complaint: ${complaintUrl}

Log in to Byte Quality Complaints to view the full conversation and respond.

© ${currentYear} Yum! Brands. All rights reserved.`,
    },
  ];
}

interface EmailTemplatePreviewProps {
  onClose: () => void;
}

export function EmailTemplatePreview({ onClose }: EmailTemplatePreviewProps) {
  const templates = generateTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('new-complaint');
  const [viewMode, setViewMode] = useState<'preview' | 'html' | 'text'>('preview');
  const [expandedInfo, setExpandedInfo] = useState(true);

  const currentTemplate = templates.find((t) => t.id === selectedTemplate)!;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
              <p className="text-sm text-gray-500">Preview system email templates</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Template List */}
          <div className="w-72 border-r border-gray-200 overflow-y-auto bg-gray-50">
            <div className="p-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 mb-2">
                Templates
              </p>
              <div className="space-y-1">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      selectedTemplate === template.id
                        ? 'bg-gray-200 text-gray-900'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span
                      className={`${selectedTemplate === template.id ? 'text-gray-600' : 'text-gray-400'}`}
                    >
                      {template.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{template.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Template Info */}
            <div className="border-b border-gray-200 bg-white">
              <button
                onClick={() => setExpandedInfo(!expandedInfo)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-400">{currentTemplate.icon}</span>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{currentTemplate.name}</h3>
                    <p className="text-sm text-gray-500">{currentTemplate.description}</p>
                  </div>
                </div>
                {expandedInfo ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              {expandedInfo && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-600">Subject:</span>
                    <span className="text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                      {currentTemplate.subject}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* View Mode Tabs */}
            <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setViewMode('preview')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'preview'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={() => setViewMode('html')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'html'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Code className="w-4 h-4" />
                HTML
              </button>
              <button
                onClick={() => setViewMode('text')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'text'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Package className="w-4 h-4" />
                Plain Text
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              {viewMode === 'preview' && (
                <div className="bg-white shadow-lg rounded-lg overflow-hidden max-w-[600px] mx-auto">
                  <iframe
                    srcDoc={currentTemplate.html}
                    className="w-full h-[600px] border-0"
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              )}
              {viewMode === 'html' && (
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono whitespace-pre-wrap">
                  {currentTemplate.html.trim()}
                </pre>
              )}
              {viewMode === 'text' && (
                <pre className="bg-white p-4 rounded-lg overflow-auto text-sm font-mono whitespace-pre-wrap border border-gray-200">
                  {currentTemplate.plainText.trim()}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailTemplatePreview;
